import React, { useState, useEffect } from "react";
import styled from "styled-components";
import * as Sentry from "@sentry/react";
import CurrentUser from "../apiResources/CurrentUser";
import RecruiterUser from "../apiResources/RecruiterUser";
import BasePage from "./BasePage";
import Alert from "./library/Alert";
import NameSettingOption from "./settings/NameSettingOption";
import EmailSettingOption from "./settings/EmailSettingOption";
import JobNotificationSettingOption from "./settings/JobNotificationSettingOption";
import JobTypeSettingOption from "./settings/JobTypeSettingOption";
import MobileNumberSettingOption from "./settings/MobileNumberSettingOption";
import PasswordSettingOption from "./settings/PasswordSettingOption";
import SectorsSettingOption from "./settings/SectorsSettingOption";
import Divider from "./library/Divider";
import Container from "./library/Container";

const RecruiterUserSettingsPage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>(null);
  const [user, setUser] = useState<RecruiterUser>(null);
  const [editFocus, setEditFocus] = useState<string>(null);

  useEffect(() => {
    const fetchUser = async () => {
      setLoading(true);
      try {
        const { id } = (await CurrentUser.first()).data;
        const { data } = await RecruiterUser.includes({
          recruiter: ["sectors"],
        }).find(id);
        setUser(data);
      } catch (err) {
        Sentry.captureException(err);
        setError("Something went wrong trying to load your user info");
      } finally {
        setLoading(false);
      }
    };

    void fetchUser();
  }, []);

  const resetEditFocus = () => setEditFocus(null);

  const isEditing = (key) => Boolean(editFocus) && editFocus === key;
  const isDisabled = (key) => Boolean(editFocus) && editFocus !== key;

  return (
    <BasePage title="Your Account">
      <Container>
        {error && <Alert status="error">{error}</Alert>}

        <OptionsList>
          <SettingGroupHeading>Your Details</SettingGroupHeading>
          <NameSettingOption
            user={user?.dup()}
            isLoading={loading}
            isEditing={isEditing("name")}
            disabled={isDisabled("name")}
            onEdit={() => setEditFocus("name")}
            onUserChange={(value) => setUser(value)}
            onDone={resetEditFocus}
          />
          <Divider />
          <EmailSettingOption
            user={user?.dup()}
            isLoading={loading}
            isEditing={isEditing("email")}
            disabled={isDisabled("email")}
            onEdit={() => setEditFocus("email")}
            onUserChange={(value) => setUser(value)}
            onDone={resetEditFocus}
          />
          <Divider />
          <PasswordSettingOption
            user={user?.dup()}
            isLoading={loading}
            isEditing={isEditing("password")}
            disabled={isDisabled("password")}
            onEdit={() => setEditFocus("password")}
            onUserChange={(value) => setUser(value)}
            onDone={resetEditFocus}
          />
          <Divider />
          <MobileNumberSettingOption
            user={user?.dup()}
            isLoading={loading}
            isEditing={isEditing("mobileNumber")}
            disabled={isDisabled("mobileNumber")}
            onEdit={() => setEditFocus("mobileNumber")}
            onUserChange={(value) => setUser(value)}
            onDone={resetEditFocus}
          />
          <Divider />

          <SettingGroupHeading>Settings</SettingGroupHeading>
          <JobTypeSettingOption
            user={user?.dup()}
            isLoading={loading}
            isEditing={isEditing("jobType")}
            disabled={isDisabled("jobType")}
            onEdit={() => setEditFocus("jobType")}
            onUserChange={(value) => setUser(value)}
            onDone={resetEditFocus}
          />
          <Divider />
          <JobNotificationSettingOption
            user={user?.dup()}
            isLoading={loading}
            disabled={isDisabled("jobNotification")}
            onUserChange={(value) => setUser(value)}
          />
          <Divider />
          <SectorsSettingOption user={user} isLoading={loading} />
        </OptionsList>
      </Container>
    </BasePage>
  );
};

const OptionsList = styled.div`
  max-width: 100%;
`;

const SettingGroupHeading = styled.h2`
  font-size: 1.75rem;
  font-weight: bold;
  margin-bottom: 1.5rem;

  @media screen and (max-width: 600px) {
    font-size: 1.5rem;
  }

  :not(:first-of-type) {
    margin-top: 3rem;
  }
`;

export default RecruiterUserSettingsPage;
